import { useMemo, useState } from 'react'

import { Skeleton, Table } from '@mantine/core'
import { ArrowDown, ArrowUp } from 'lucide-react'

import { cn } from 'src/utils'

/**
 * Generic, domain-neutral leaderboard table.
 *
 * Configured by the consumer via `columns` + `rows`. Knows nothing about
 * employees, sources, or any specific metric — reusable for any
 * "ranked rows of metrics" view.
 *
 * Style parity with the Marketing tab tables (SourceBreakdownCardView,
 * PipelineOutcomesTable): `verticalSpacing="xs" horizontalSpacing="sm" fz="xs"`,
 * no AnalyticsCard wrapper at this layer (the consumer wraps if needed).
 */

export interface LeaderboardColumn<T> {
  /** Property of the row used for cell access AND as the sort key. */
  key: keyof T & string
  label: string
  /** Alignment of the cell + header. Defaults to 'right' (numeric metrics). */
  align?: 'left' | 'right'
  /**
   * Custom cell renderer. Falls back to a sensible default
   * (toLocaleString'd numbers; "—" for null) when omitted.
   */
  render?: (row: T) => React.ReactNode
  /** Defaults to true. Set false on label/identity columns. */
  sortable?: boolean
  /** Inline width for the column (e.g. 160 for a "Volume" bar column). */
  width?: number
  /**
   * When provided, the per-row cell becomes clickable (cursor + hover
   * underline) and invokes this callback. Used for navigating to a
   * pre-filtered slice — e.g. opening the Applicants page filtered by the
   * employee in this row and the metric this column represents. Rates/
   * averages should leave this unset since there's no single cohort behind
   * the displayed value.
   */
  onCellClick?: (row: T) => void
  /**
   * When provided, the matching cell in the totals row becomes clickable.
   * Independent from `onCellClick` because the totals row isn't keyed by a
   * row object — it represents the aggregate across all rows.
   */
  onTotalsCellClick?: () => void
  /**
   * Native-title tooltip surfaced on the header. Used to disambiguate metrics
   * whose definition isn't obvious from the column label (e.g. Conv vs Cohort
   * Conv, both percentage columns that measure different things).
   */
  tooltip?: string
}

export type SortDirection = 'asc' | 'desc'

export interface LeaderboardCardViewProps<T extends { id: string }> {
  columns: ReadonlyArray<LeaderboardColumn<T>>
  rows: ReadonlyArray<T>
  loading?: boolean
  emptyMessage?: string
  /** Default sort column key. Defaults to the first sortable column. */
  defaultSortKey?: keyof T & string
  /** Default sort direction. Defaults to 'desc'. */
  defaultSortDir?: SortDirection
  /**
   * Pre-aggregated totals shown as a sticky last row. Keys must match
   * column keys so each column renders the right total. Pass undefined to
   * skip the row entirely. Custom column renderers DON'T run on this row —
   * if a column needs special formatting (e.g. percent, days), pass the
   * pre-formatted string here.
   */
  totals?: Partial<Record<keyof T & string, React.ReactNode>>
  /** Label rendered in the leftmost cell of the totals row. Defaults to "Total". */
  totalsLabel?: string
}

const compareValues = (
  a: unknown,
  b: unknown,
  direction: SortDirection
): number => {
  // Nulls always sort to the bottom regardless of direction.
  const aNull = a == null
  const bNull = b == null
  if (aNull && bNull) return 0
  if (aNull) return 1
  if (bNull) return -1

  if (typeof a === 'number' && typeof b === 'number') {
    return direction === 'asc' ? a - b : b - a
  }
  const aStr = String(a)
  const bStr = String(b)
  return direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
}

export const LeaderboardCardView = <T extends { id: string }>({
  columns,
  rows,
  loading = false,
  emptyMessage = 'No data for this period.',
  defaultSortKey,
  defaultSortDir = 'desc',
  totals,
  totalsLabel = 'Total',
}: LeaderboardCardViewProps<T>) => {
  const initialSortKey = useMemo<(keyof T & string) | undefined>(() => {
    if (defaultSortKey) return defaultSortKey
    const firstSortable = columns.find((c) => c.sortable !== false)
    return firstSortable?.key
  }, [columns, defaultSortKey])

  const [sortKey, setSortKey] = useState<(keyof T & string) | undefined>(
    initialSortKey
  )
  const [sortDir, setSortDir] = useState<SortDirection>(defaultSortDir)

  const sortedRows = useMemo(() => {
    if (!sortKey) return [...rows]
    return [...rows].sort((a, b) =>
      compareValues(a[sortKey], b[sortKey], sortDir)
    )
  }, [rows, sortKey, sortDir])

  const handleSort = (key: keyof T & string) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
      return
    }
    setSortKey(key)
    setSortDir('desc')
  }

  if (loading) {
    return (
      <Table
        verticalSpacing="xs"
        horizontalSpacing="sm"
        fz="xs"
        data-testid="leaderboard-table-loading"
      >
        <Table.Tbody>
          {Array.from({ length: 5 }).map((_, i) => (
            <Table.Tr key={i}>
              {columns.map((col, j) => (
                <Table.Td key={col.key + i + j}>
                  <Skeleton height={12} width={j === 0 ? '60%' : '50%'} />
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    )
  }

  if (sortedRows.length === 0) {
    return (
      <div className="px-4 py-8 text-center text-xs text-doubleNickel-gray-500">
        {emptyMessage}
      </div>
    )
  }

  return (
    <Table
      verticalSpacing="xs"
      horizontalSpacing="sm"
      fz="xs"
      data-testid="leaderboard-table"
    >
      <Table.Thead>
        <Table.Tr>
          {columns.map((col) => (
            <SortableHeader
              key={col.key}
              column={col}
              activeSortKey={sortKey}
              activeDirection={sortDir}
              onSort={handleSort}
            />
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {sortedRows.map((row) => (
          <Table.Tr key={row.id}>
            {columns.map((col) => {
              const clickable = Boolean(col.onCellClick)
              return (
                <Table.Td
                  key={col.key}
                  className={cn(
                    'tabular-nums',
                    (col.align ?? 'right') === 'right'
                      ? 'text-right'
                      : 'text-left',
                    clickable && 'cursor-pointer hover:underline'
                  )}
                  onClick={
                    clickable ? () => col.onCellClick?.(row) : undefined
                  }
                >
                  {col.render ? col.render(row) : renderDefault(row[col.key])}
                </Table.Td>
              )
            })}
          </Table.Tr>
        ))}

        {totals && (
          <Table.Tr
            className="border-t-2 border-doubleNickel-gray-200 bg-doubleNickel-gray-25"
            data-testid="leaderboard-totals-row"
          >
            {columns.map((col, idx) => {
              const isFirst = idx === 0
              const align = col.align ?? 'right'
              const cellValue = isFirst ? totalsLabel : totals[col.key]
              const clickable = !isFirst && Boolean(col.onTotalsCellClick)
              return (
                <Table.Td
                  key={col.key}
                  className={cn(
                    'tabular-nums font-semibold text-doubleNickel-gray-900',
                    align === 'right' ? 'text-right' : 'text-left',
                    clickable && 'cursor-pointer hover:underline'
                  )}
                  onClick={
                    clickable ? () => col.onTotalsCellClick?.() : undefined
                  }
                >
                  {cellValue ?? ''}
                </Table.Td>
              )
            })}
          </Table.Tr>
        )}
      </Table.Tbody>
    </Table>
  )
}

interface SortableHeaderProps<T> {
  column: LeaderboardColumn<T>
  activeSortKey: (keyof T & string) | undefined
  activeDirection: SortDirection
  onSort: (key: keyof T & string) => void
}

const SortableHeader = <T,>({
  column,
  activeSortKey,
  activeDirection,
  onSort,
}: SortableHeaderProps<T>) => {
  const align = column.align ?? 'right'
  const sortable = column.sortable !== false
  const isActive = sortable && column.key === activeSortKey
  const Icon = activeDirection === 'asc' ? ArrowUp : ArrowDown
  const widthStyle = column.width ? { width: column.width } : undefined
  return (
    <Table.Th
      className={cn(
        'select-none text-doubleNickel-gray-600',
        sortable
          ? 'cursor-pointer hover:text-doubleNickel-gray-900'
          : 'cursor-default',
        align === 'right' ? 'text-right' : 'text-left'
      )}
      style={widthStyle}
      onClick={sortable ? () => onSort(column.key) : undefined}
      title={column.tooltip}
    >
      <span
        className={cn(
          'inline-flex items-center gap-1',
          align === 'right' && 'flex-row-reverse'
        )}
      >
        {column.label}
        {isActive && <Icon size={12} className="opacity-70" />}
      </span>
    </Table.Th>
  )
}

const renderDefault = (value: unknown): React.ReactNode => {
  if (value == null) return '—'
  if (typeof value === 'number') return value.toLocaleString()
  return String(value)
}
